import { call, put } from 'redux-saga/effects';
import { requestGetProjectSummary, requestGetProject, requestGetProjects, requestGetProjectBillingEntities, requestSetProjectAssignees, requestSetProjectResponsible, requestSetProjectExternalBoard, requestUpdateProject, requestCreateProject, requestUpdateProjectStatus, requestCompleteProject, requestDeleteProject, requestRestoreProject, requestGetUnansweredClientComments, requestGetMonthlyStatusBreakdown, requestGetUnassignedSummary, requestGetUnassignedList, requestGetProjectMovePreview, requestMoveProject, requestAuthorizeProject, requestRejectProject } from '../requests/project';
import { setCurrentProject, setProjectCompleted, setProjectStatus, updateProjectAssignees, updateProjectResponsible, updateProjectState } from '../../slices/project';
import { showAlert } from '../../slices/alerts';
import { safeErrorMessage } from '../../../utils/errorUtils';

export function* handleGetProjectSummary(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(requestGetProjectSummary);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: "error", message: safeErrorMessage(error) }));
    }
}

export function* handleGetProject(action) {
    try {
        const { projectId, callback } = action.payload;
        const response = yield call(requestGetProject, projectId);
        if (callback) {
            callback(response.data);
        } else {
            yield put(setCurrentProject(response.data));
        }
    } catch (error) {
        yield put(showAlert({ severity: "error", message: safeErrorMessage(error) }));
    }
}

export function* handleGetProjects(action) {
    try {
        const { start, limit, state = 'open', filters, callback } = action.payload;
        const response = yield call(requestGetProjects, start, limit, state, filters);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleGetProjectBillingEntities(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(requestGetProjectBillingEntities);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleUpdateProject(action) {
    try {
        const { projectId, projectData, callback } = action.payload;
        const response = yield call(requestUpdateProject, projectId, projectData);
        yield put(updateProjectState(response.data));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleSetProjectExternalBoard(action) {
    try {
        const { projectId, externalBoard, callback } = action.payload;
        const response = yield call(requestSetProjectExternalBoard, projectId, externalBoard);
        yield put(updateProjectState({ project: { externalBoard: response.data.externalBoard } }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (callback) {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleSetProjectResponsible(action) {
    try {
        const { projectId, responsible, callback } = action.payload;
        const response = yield call(requestSetProjectResponsible, projectId, responsible);
        if (callback) {
            callback(response.data);
        } else {
            yield put(updateProjectResponsible(response.data));
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleSetProjectAssignees(action) {
    try {
        const { projectId, responsible, assignees, callback } = action.payload;
        const response = yield call(requestSetProjectAssignees, projectId, responsible, assignees);
        if (callback) {
            callback(response.data);
        } else {
            yield put(updateProjectAssignees(response.data));
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCreateProject(action) {
    try {
        const { projectData, callback } = action.payload;
        const response = yield call(requestCreateProject, projectData);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleUpdateProjectStatus(action) {
    try {
        const { projectId, status, callback } = action.payload;
        const response = yield call(requestUpdateProjectStatus, projectId, status);
        if (callback) {
            callback(response.data);
        } else {
            yield put(setProjectStatus(response.data))
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCompleteProject(action) {
    try {
        const { projectId, options, callback } = action.payload;
        const response = yield call(requestCompleteProject, projectId, options);
        yield put(setProjectCompleted(response.data));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload;
        if (callback) {
            callback(null);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleDeleteProject(action) {
    try {
        const { projectId, callback } = action.payload;
        const response = yield call(requestDeleteProject, projectId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleRestoreProject(action) {
    try {
        const { projectId, callback } = action.payload;
        const response = yield call(requestRestoreProject, projectId);
        if (callback) {
            callback(response.data);
        } else {
            yield put(setCurrentProject(response.data.project));
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleGetProjectMovePreview(action) {
    try {
        const { projectId, accountId, callback } = action.payload;
        const response = yield call(requestGetProjectMovePreview, projectId, accountId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (callback) {
            callback(null);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleMoveProject(action) {
    try {
        const { projectId, accountId, callback } = action.payload;
        const response = yield call(requestMoveProject, projectId, accountId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (callback) {
            callback(null);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

function* resolveProjectAuthorization(request, action) {
    try {
        const { projectId, callback } = action.payload;
        const response = yield call(request, projectId);
        yield put(setProjectStatus(response.data));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (callback) {
            callback(null);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleAuthorizeProject(action) {
    yield* resolveProjectAuthorization(requestAuthorizeProject, action);
}

export function* handleRejectProject(action) {
    yield* resolveProjectAuthorization(requestRejectProject, action);
}

export function* handleGetMonthlyStatusBreakdown(action) {
    try {
        const { months, callback } = action.payload || {};
        const response = yield call(requestGetMonthlyStatusBreakdown, months);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback([]);
        }
    }
}

export function* handleGetUnansweredClientComments(action) {
    try {
        const { callback } = action.payload || {};
        const response = yield call(requestGetUnansweredClientComments);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback([]);
        }
    }
}


export function* handleGetUnassignedSummary(action) {
    try {
        const { callback } = action.payload || {};
        const response = yield call(requestGetUnassignedSummary);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleGetUnassignedList(action) {
    try {
        const { type, cell, start, callback } = action.payload || {};
        const response = yield call(requestGetUnassignedList, type, cell, start);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}
